import {
  AccessRequest,
  ActiveRuns,
  AlertRule,
  type AlertRuleInput,
  type AlertsAction,
  AlertsPage,
  AuditPage,
  BillingPage,
  Channel,
  type ChannelInput,
  type ChannelsAction,
  ChannelTestResult,
  CompiledSql,
  ConnectionDetail,
  type ConnectionInput,
  ConnectionPick,
  ConnectionSummary,
  type ConnectionsAction,
  ConnectionTest,
  type ConnectionUpdate,
  EnvironmentConfig,
  type EnvironmentInput,
  type EnvironmentsAction,
  EnvironmentsPage,
  FailingRows,
  type GovernanceInput,
  HomeSummary,
  InviteAccepted,
  InviteDetail,
  IssueDetail,
  type IssuesAction,
  IssuesPage,
  type JobAction,
  JobDetail,
  type JobInput,
  JobsPage,
  LineageGraph,
  Me,
  type MembersAction,
  MembersOverview,
  MembersPeoplePage,
  ModelColumns,
  ModelDetail,
  ModelRuns,
  ModelSql,
  type NewProjectInput,
  Notification,
  PendingInvite,
  ProjectBadges,
  ProjectDetail,
  ProjectGovernance,
  type ProjectSettingsAction,
  ProjectSettingsGeneralTab,
  ProjectSettingsMembersTab,
  ProjectSettingsPage,
  ProjectSettingsTokensTab,
  ProjectSummary,
  QualityActionResult,
  RepoCandidatePage,
  RepositoryPage,
  RepositorySettings,
  RoleDefinition,
  type RunAction,
  RunArtifactsTab,
  RunDetail,
  RunModelsTab,
  RunRow,
  RunSourcesTab,
  RunStatusUpdate,
  RunsPage,
  type RunsQuery,
  RunTestsTab,
  type RunTriggerRequest,
  SearchIndex,
  ServiceAccount,
  SlugAvailability,
  SourceChecks,
  type SourcesAction,
  SourcesPage,
  StepLogs,
  Team,
  TestDetail,
  type TestsAction,
  TestsPage,
  WorkspaceChannel,
  WorkspaceCreated,
  type WorkspaceCreateInput,
  WorkspaceGroup,
  WorkspaceMember,
  WorkspaceProject,
  type WorkspaceProjectsAction,
  WorkspaceProjectsPage,
  type WorkspaceSettingsAction,
  WorkspaceSettingsPage,
} from "@auradata/contracts";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { api } from "./client";

const WorkspaceOverview = z.object({
  workspace: z.object({ id: z.string(), slug: z.string(), name: z.string() }),
  projects: z.array(ProjectSummary),
});
export type WorkspaceOverview = z.infer<typeof WorkspaceOverview>;

export const meQuery = () =>
  queryOptions({ queryKey: ["me"], queryFn: () => api("/api/me", Me), staleTime: 60_000 });

export const workspaceQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["workspace", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}`, WorkspaceOverview),
  });

export const projectQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project", workspace, project],
    queryFn: () => api(`/api/workspaces/${workspace}/projects/${project}`, ProjectDetail),
  });

export const homeQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["home", workspace, project, env],
    queryFn: () =>
      api(`/api/workspaces/${workspace}/projects/${project}/home?env=${env}`, HomeSummary),
  });

/** Four sidebar badges only — not the full Home read model. No polling. */
export const projectBadgesQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project-badges", workspace, project],
    queryFn: () => api(`/api/workspaces/${workspace}/projects/${project}/badges`, ProjectBadges),
  });

// --- Onboarding: invitations and creating a workspace ------------------------------------

/** Invitations waiting for the signed-in address. Small, and only the front door asks for it. */
export const myInvitesQuery = () =>
  queryOptions({
    queryKey: ["my-invites"],
    queryFn: () => api("/api/me/invites", z.array(PendingInvite)),
  });

export const inviteQuery = (token: string) =>
  queryOptions({
    queryKey: ["invite", token],
    queryFn: () => api(`/api/invites/${encodeURIComponent(token)}`, InviteDetail),
    retry: false,
  });

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api(`/api/invites/${encodeURIComponent(token)}/accept`, InviteAccepted, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["my-invites"] });
    },
  });
}

export function useDeclineInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api(`/api/invites/${encodeURIComponent(token)}/decline`, z.object({ ok: z.boolean() }), {
        method: "POST",
      }),
    onSuccess: (_data, token) => qc.invalidateQueries({ queryKey: ["invite", token] }),
  });
}

export function useRequestInviteRenewal() {
  return useMutation({
    mutationFn: (token: string) =>
      api(
        `/api/invites/${encodeURIComponent(token)}/request-renewal`,
        z.object({ ok: z.boolean() }),
        {
          method: "POST",
        },
      ),
  });
}

/** Debounced by the caller: only asked once the slug looks well formed. */
export const slugAvailableQuery = (slug: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["slug-available", slug],
    queryFn: () =>
      api(`/api/workspaces/slug-available?slug=${encodeURIComponent(slug)}`, SlugAvailability),
    enabled,
    staleTime: 30_000,
  });

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkspaceCreateInput) =>
      api("/api/workspaces", WorkspaceCreated, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api("/api/auth/logout", z.object({ ok: z.boolean() }), { method: "POST" }),
    onSuccess: () => location.assign("/sign-in"),
  });
}

export const notificationsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["notifications", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}/notifications`, z.array(Notification)),
  });

export const searchIndexQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["search-index", workspace, project],
    queryFn: () =>
      api(`/api/workspaces/${workspace}/projects/${project}/search-index`, SearchIndex),
    staleTime: 60_000,
  });

export function useTriggerRun(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RunTriggerRequest) =>
      api(`/api/workspaces/${workspace}/projects/${project}/runs`, RunRow, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs-active", workspace, project] });
      qc.invalidateQueries({ queryKey: ["workspace", workspace] });
    },
  });
}

export function useMarkNotificationsRead(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[] | "all") =>
      api(`/api/workspaces/${workspace}/notifications/read`, z.object({ ok: z.boolean() }), {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", workspace] }),
  });
}

// --- Runs ------------------------------------------------------------------

const base = (workspace: string, project: string) =>
  `/api/workspaces/${workspace}/projects/${project}`;

export const runsQuery = (workspace: string, project: string, q: RunsQuery) =>
  queryOptions({
    queryKey: ["runs", workspace, project, q],
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") sp.set(k, String(v));
      return api(`${base(workspace, project)}/runs?${sp}`, RunsPage);
    },
    placeholderData: (prev) => prev,
  });

export const runDetailQuery = (workspace: string, project: string, runId: string) =>
  queryOptions({
    queryKey: ["run", workspace, project, runId],
    queryFn: () => api(`${base(workspace, project)}/runs/${runId}`, RunDetail),
  });

/** < 500 B poll while the run moves; the page refetches in full once it goes terminal. */
export const runStatusQuery = (workspace: string, project: string, runId: string, live: boolean) =>
  queryOptions({
    queryKey: ["run-status", workspace, project, runId],
    queryFn: () => api(`${base(workspace, project)}/runs/${runId}/status`, RunStatusUpdate),
    enabled: live,
    refetchInterval: live ? 1_000 : false,
  });

/** Watches one run and refetches the detail (and its logs) once when it stops moving. */
export function useRunStatus(workspace: string, project: string, runId: string, live: boolean) {
  const qc = useQueryClient();
  const { data } = useQuery(runStatusQuery(workspace, project, runId, live));
  const terminal = data ? data.status !== "running" && data.status !== "queued" : false;
  useEffect(() => {
    if (!live || !terminal) return;
    qc.invalidateQueries({ queryKey: ["run", workspace, project, runId] });
    qc.invalidateQueries({ queryKey: ["run-logs", workspace, project, runId] });
    qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
  }, [live, terminal, qc, workspace, project, runId]);
  return data ?? null;
}

/** One step's log from line `since` on: every line reaches the browser exactly once. */
export const stepLogsQuery = (
  workspace: string,
  project: string,
  runId: string,
  step: number,
  since: number,
  streaming: boolean,
) =>
  queryOptions({
    queryKey: ["run-logs", workspace, project, runId, step, since],
    queryFn: () =>
      api(`${base(workspace, project)}/runs/${runId}/logs?step=${step}&since=${since}`, StepLogs),
    refetchInterval: streaming ? 700 : false,
  });

const runTab = <T>(
  workspace: string,
  project: string,
  runId: string,
  tab: string,
  schema: z.ZodType<T>,
  enabled: boolean,
  live: boolean,
) =>
  queryOptions({
    queryKey: ["run-tab", workspace, project, runId, tab],
    queryFn: () => api(`${base(workspace, project)}/runs/${runId}/${tab}`, schema),
    enabled,
    // A finished run never changes: load each tab once.
    staleTime: live ? 0 : Number.POSITIVE_INFINITY,
  });

export const runModelsQuery = (
  workspace: string,
  project: string,
  runId: string,
  enabled: boolean,
  live: boolean,
) => runTab(workspace, project, runId, "models", RunModelsTab, enabled, live);

export const runTestsQuery = (
  workspace: string,
  project: string,
  runId: string,
  enabled: boolean,
  live: boolean,
) => runTab(workspace, project, runId, "tests", RunTestsTab, enabled, live);

export const runSourcesQuery = (
  workspace: string,
  project: string,
  runId: string,
  enabled: boolean,
  live: boolean,
) => runTab(workspace, project, runId, "sources", RunSourcesTab, enabled, live);

export const runArtifactsQuery = (
  workspace: string,
  project: string,
  runId: string,
  enabled: boolean,
  live: boolean,
) => runTab(workspace, project, runId, "artifacts", RunArtifactsTab, enabled, live);

/** The only thing Home, Runs and Jobs poll: the runs still in flight (< 300 B). */
export const activeRunsQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["runs-active", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/runs/active?env=${env}`, ActiveRuns),
    refetchInterval: (q) => (q.state.data?.running ? 2_000 : false),
  });

/**
 * Live run state for a whole screen: polls the small endpoint and refetches the screen's own
 * payload once, when the last run finishes.
 */
export function useActiveRuns(workspace: string, project: string, env: string) {
  const qc = useQueryClient();
  const { data } = useQuery(activeRunsQuery(workspace, project, env));
  const running = data?.running ?? 0;
  const previous = useRef(running);
  useEffect(() => {
    if (previous.current > 0 && running === 0) {
      for (const key of ["runs", "home", "jobs"])
        qc.invalidateQueries({ queryKey: [key, workspace, project] });
    }
    previous.current = running;
  }, [running, qc, workspace, project]);
  return data ?? { running: 0, runs: [] };
}

export function useRunAction(workspace: string, project: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: RunAction["action"]) =>
      api(`${base(workspace, project)}/runs/${runId}/actions`, z.object({ ok: z.boolean() }), {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["run", workspace, project, runId] });
      qc.invalidateQueries({ queryKey: ["run-logs", workspace, project, runId] });
      qc.invalidateQueries({ queryKey: ["run-tab", workspace, project, runId] });
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs-active", workspace, project] });
      qc.invalidateQueries({ queryKey: ["home", workspace, project] });
    },
  });
}

// --- Test / model detail views --------------------------------------------

export const failingRowsQuery = (
  workspace: string,
  project: string,
  test: string,
  model: string,
  total: number,
) =>
  queryOptions({
    queryKey: ["test-rows", workspace, project, test],
    queryFn: () =>
      api(
        `${base(workspace, project)}/tests/${encodeURIComponent(test)}/rows?model=${model}&total=${total}`,
        FailingRows,
      ),
  });

export const compiledSqlQuery = (workspace: string, project: string, test: string, model: string) =>
  queryOptions({
    queryKey: ["test-sql", workspace, project, test],
    queryFn: () =>
      api(
        `${base(workspace, project)}/tests/${encodeURIComponent(test)}/sql?model=${model}`,
        CompiledSql,
      ),
  });

// --- Lineage and Model detail ---------------------------------------------

/** The whole graph for one environment: nodes, edges and how the last run went. */
export const lineageQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["lineage", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/lineage?env=${env}`, LineageGraph),
  });

const modelPath = (workspace: string, project: string, model: string) =>
  `${base(workspace, project)}/models/${encodeURIComponent(model)}`;

/** Header, decision strip, facts and neighbours — also what the Lineage side panel shows. */
export const modelDetailQuery = (
  workspace: string,
  project: string,
  model: string | null,
  env: string,
) =>
  queryOptions({
    queryKey: ["model", workspace, project, model, env],
    queryFn: () => api(`${modelPath(workspace, project, model ?? "")}?env=${env}`, ModelDetail),
    enabled: model !== null,
  });

/** Loaded when the Columns tab is shown, never with the header. */
export const modelColumnsQuery = (
  workspace: string,
  project: string,
  model: string,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["model-columns", workspace, project, model],
    queryFn: () => api(`${modelPath(workspace, project, model)}/columns`, ModelColumns),
    enabled,
  });

export const modelRunsQuery = (
  workspace: string,
  project: string,
  model: string,
  env: string,
  page: number,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["model-runs", workspace, project, model, env, page],
    queryFn: () =>
      api(`${modelPath(workspace, project, model)}/runs?env=${env}&page=${page}`, ModelRuns),
    enabled,
    placeholderData: (prev) => prev,
  });

export const modelSqlQuery = (
  workspace: string,
  project: string,
  model: string,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["model-sql", workspace, project, model],
    queryFn: () => api(`${modelPath(workspace, project, model)}/sql`, ModelSql),
    enabled,
  });

/** Queues a real `dbt build --select <selector>` for one node. */
export function useRunModel(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ model, selector, env }: { model: string; selector: string; env: string }) =>
      api(
        `${modelPath(workspace, project, model)}/run?selector=${encodeURIComponent(selector)}&env=${env}`,
        QualityActionResult,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs-active", workspace, project] });
    },
  });
}

// --- Jobs ------------------------------------------------------------------

export const channelsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["channels", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}/channels`, z.array(Channel)),
    staleTime: 60_000,
  });

export const jobsQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["jobs", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/jobs?env=${env}`, JobsPage),
  });

/** The drawer and the edit form load one job; the list row carries no drawer data. */
export const jobQuery = (workspace: string, project: string, jobId: string | null) =>
  queryOptions({
    queryKey: ["job", workspace, project, jobId],
    queryFn: () => api(`${base(workspace, project)}/jobs/${jobId}`, JobDetail),
    enabled: jobId !== null,
  });

export function useSaveJob(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: JobInput }) =>
      api(
        id ? `${base(workspace, project)}/jobs/${id}` : `${base(workspace, project)}/jobs`,
        JobDetail,
        {
          method: id ? "PUT" : "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["job", workspace, project] });
      qc.invalidateQueries({ queryKey: ["search-index", workspace, project] });
    },
  });
}

export function useJobAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & JobAction) =>
      api(
        `${base(workspace, project)}/jobs/${id}/actions`,
        z.object({ ok: z.boolean(), run: RunRow.nullable() }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["job", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs-active", workspace, project] });
      qc.invalidateQueries({ queryKey: ["home", workspace, project] });
    },
  });
}

// --- Tests screen ------------------------------------------------------------

export const testsQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["tests", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/tests?env=${env}`, TestsPage),
  });

/** Loaded when a test row is expanded, never with the list. */
export const testDetailQuery = (
  workspace: string,
  project: string,
  test: string | null,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["test-detail", workspace, project, test],
    queryFn: () =>
      api(`${base(workspace, project)}/tests/${encodeURIComponent(test ?? "")}/recent`, TestDetail),
    enabled: enabled && test !== null,
    staleTime: 30_000,
  });

export function useTestsAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TestsAction) =>
      api(`${base(workspace, project)}/tests/actions`, QualityActionResult, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tests", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
    },
  });
}

// --- Sources / Issues / Alerts ---------------------------------------------

export const sourcesQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["sources", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/sources?env=${env}`, SourcesPage),
  });

/** Loaded when a source row is expanded, never with the list. */
export const sourceChecksQuery = (
  workspace: string,
  project: string,
  name: string | null,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["source-checks", workspace, project, name],
    queryFn: () =>
      api(
        `${base(workspace, project)}/sources/${encodeURIComponent(name ?? "")}/checks`,
        SourceChecks,
      ),
    enabled: enabled && name !== null,
    staleTime: 30_000,
  });

export function useSourcesAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourcesAction) =>
      api(`${base(workspace, project)}/sources/actions`, QualityActionResult, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", workspace, project] });
      qc.invalidateQueries({ queryKey: ["runs", workspace, project] });
    },
  });
}

export const issuesQuery = (workspace: string, project: string, env: string) =>
  queryOptions({
    queryKey: ["issues", workspace, project, env],
    queryFn: () => api(`${base(workspace, project)}/issues?env=${env}`, IssuesPage),
  });

/** Loaded when an issue row is expanded, never with the list. */
export const issueDetailQuery = (
  workspace: string,
  project: string,
  id: string | null,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ["issue-detail", workspace, project, id],
    queryFn: () =>
      api(`${base(workspace, project)}/issues/${encodeURIComponent(id ?? "")}`, IssueDetail),
    enabled: enabled && id !== null,
    staleTime: 30_000,
  });

export function useIssuesAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssuesAction) =>
      api(`${base(workspace, project)}/issues/actions`, z.object({ ok: z.boolean() }), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["issues", workspace, project] }),
  });
}

export const alertsQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["alerts", workspace, project],
    queryFn: () => api(`${base(workspace, project)}/alerts`, AlertsPage),
  });

export function useSaveRule(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: AlertRuleInput }) =>
      api(
        id ? `${base(workspace, project)}/alerts/${id}` : `${base(workspace, project)}/alerts`,
        AlertRule,
        {
          method: id ? "PUT" : "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", workspace, project] }),
  });
}

export function useAlertsAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AlertsAction) =>
      api(`${base(workspace, project)}/alerts/actions`, z.object({ ok: z.boolean() }), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", workspace, project] }),
  });
}

/** Sends a real message through the channel and records it as a delivery. */
export function useTestChannel(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`${base(workspace, project)}/channels/${id}/test`, ChannelTestResult, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", workspace, project] }),
  });
}

// --- Workspace fixtures ------------------------------------------------------

export const connectionsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["connections", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}/connections`, z.array(ConnectionSummary)),
    staleTime: 60_000,
    // Keep rows live while any connection test runs on a runner.
    refetchInterval: (q) => (q.state.data?.some((c) => c.health === "testing") ? 2_000 : false),
  });

/** Pick-list rows only (`id`, `name`, `type`, `account`); loaded when a Picker opens. */
export const connectionPicksQuery = (workspace: string, q: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["connection-picks", workspace, q],
    queryFn: () =>
      api(
        `${ws(workspace)}/connections?fields=pick&limit=20&q=${encodeURIComponent(q)}`,
        z.array(ConnectionPick),
      ),
    enabled,
    staleTime: 60_000,
  });
export const teamsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["teams", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}/teams`, z.array(Team)),
    staleTime: 60_000,
  });
export const membersQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["members", workspace],
    queryFn: () => api(`/api/workspaces/${workspace}/members`, z.array(WorkspaceMember)),
    staleTime: 60_000,
  });

// --- Environments / Repository / Settings ---------------------------------------

export const environmentsQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["environments", workspace, project],
    queryFn: () => api(`${base(workspace, project)}/environments`, EnvironmentsPage),
    refetchInterval: (q) => (q.state.data?.inUse.some((c) => c.testing) ? 600 : false),
  });

export function useSaveEnvironment(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: EnvironmentInput }) =>
      api(
        id
          ? `${base(workspace, project)}/environments/${id}`
          : `${base(workspace, project)}/environments`,
        EnvironmentConfig,
        { method: id ? "PUT" : "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["environments", workspace, project] });
      qc.invalidateQueries({ queryKey: ["project", workspace, project] });
    },
  });
}

export function useEnvironmentsAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EnvironmentsAction) =>
      api(`${base(workspace, project)}/environments/actions`, z.object({ ok: z.boolean() }), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["environments", workspace, project] }),
  });
}

export const repositoryQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["repository", workspace, project],
    queryFn: () => api(`${base(workspace, project)}/repository`, RepositoryPage),
  });

export function useSaveRepository(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RepositorySettings) =>
      api(`${base(workspace, project)}/repository/settings`, RepositorySettings, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repository", workspace, project] }),
  });
}

export const projectSettingsQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project-settings", workspace, project],
    queryFn: () => api(`${base(workspace, project)}/settings`, ProjectSettingsPage),
  });

export const projectSettingsGeneralQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project-settings", workspace, project, "general"],
    queryFn: () => api(`${base(workspace, project)}/settings/general`, ProjectSettingsGeneralTab),
  });

export const projectSettingsMembersQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project-settings", workspace, project, "members"],
    queryFn: () => api(`${base(workspace, project)}/settings/members`, ProjectSettingsMembersTab),
  });

export const projectSettingsTokensQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["project-settings", workspace, project, "tokens"],
    queryFn: () => api(`${base(workspace, project)}/settings/tokens`, ProjectSettingsTokensTab),
  });

export function useProjectSettingsAction(workspace: string, project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectSettingsAction) =>
      api(
        `${base(workspace, project)}/settings/actions`,
        z.object({ ok: z.boolean(), token: z.string().optional() }),
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: (_data, body) => {
      // Invalidate only the tab the action touched, not the whole settings read model.
      const base = ["project-settings", workspace, project] as const;
      switch (body.action) {
        case "save_general":
          qc.invalidateQueries({ queryKey: [...base, "general"] });
          qc.invalidateQueries({ queryKey: base, exact: true });
          qc.invalidateQueries({ queryKey: ["project", workspace, project] });
          break;
        case "add_member":
        case "set_role":
        case "remove_member":
        case "revoke_invite":
          qc.invalidateQueries({ queryKey: [...base, "members"] });
          break;
        case "create_token":
        case "revoke_token":
          qc.invalidateQueries({ queryKey: [...base, "tokens"] });
          break;
        case "set_notification":
        case "pause":
        case "resume":
          qc.invalidateQueries({ queryKey: base, exact: true });
          break;
        default:
          break;
      }
    },
  });
}

// --- Workspace screens ----------------------------------------------------------

const ws = (workspace: string) => `/api/workspaces/${workspace}`;
const okSchema = z.object({ ok: z.boolean(), token: z.string().optional() });

export const workspaceProjectsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["ws-projects", workspace],
    queryFn: () => api(`${ws(workspace)}/projects-overview`, WorkspaceProjectsPage),
  });

/** Loaded by the governance drawer only — grants never travel with the list. */
export const governanceQuery = (workspace: string, project: string) =>
  queryOptions({
    queryKey: ["governance", workspace, project],
    queryFn: () => api(`${ws(workspace)}/projects/${project}/governance`, ProjectGovernance),
  });

export const projectExistsQuery = (workspace: string, slug: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["project-exists", workspace, slug],
    queryFn: () =>
      api(`${ws(workspace)}/projects/${slug}/exists`, z.object({ exists: z.boolean() })),
    enabled,
    staleTime: 30_000,
  });

export function useGovernance(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: GovernanceInput }) =>
      api(`${ws(workspace)}/projects/${id}/governance`, ProjectGovernance, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: (g) => {
      qc.setQueryData(["governance", workspace, g.slug], g);
      qc.invalidateQueries({ queryKey: ["ws-projects", workspace] });
      qc.invalidateQueries({ queryKey: ["audit", workspace] });
    },
  });
}

export function useWorkspaceProjectsAction(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceProjectsAction) =>
      api(`${ws(workspace)}/projects-actions`, okSchema, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ws-projects", workspace] });
      qc.invalidateQueries({ queryKey: ["workspace", workspace] });
    },
  });
}

/** Server-side paged repository list; the client never slices a 214-row payload. */
export const reposQuery = (workspace: string, q: string, limit: number) =>
  queryOptions({
    queryKey: ["repos", workspace, q, limit],
    queryFn: () =>
      api(
        `${ws(workspace)}/repositories?limit=${limit}&q=${encodeURIComponent(q)}`,
        RepoCandidatePage,
      ),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

export function useCreateProject(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewProjectInput) =>
      api(`${ws(workspace)}/projects`, WorkspaceProject, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ws-projects", workspace] });
      qc.invalidateQueries({ queryKey: ["workspace", workspace] });
    },
  });
}

export const connectionQuery = (workspace: string, id: string) =>
  queryOptions({
    queryKey: ["connection", workspace, id],
    queryFn: () => api(`${ws(workspace)}/connections/${id}`, ConnectionDetail),
    staleTime: 30_000,
  });

export const connectionTestQuery = (workspace: string, id: string) =>
  queryOptions({
    queryKey: ["connection-test", workspace, id],
    queryFn: () => api(`${ws(workspace)}/connections/${id}/test`, ConnectionTest.nullable()),
  });

export function useUpdateConnection(workspace: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectionUpdate) =>
      api(`${ws(workspace)}/connections/${id}`, ConnectionDetail, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: (detail) => {
      qc.setQueryData(["connection", workspace, id], detail);
      qc.invalidateQueries({ queryKey: ["connections", workspace] });
    },
  });
}

export function useTestConnection(workspace: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api(`${ws(workspace)}/connections/${id}/test`, ConnectionTest, { method: "POST" }),
    onSuccess: (test) => {
      qc.setQueryData(["connection-test", workspace, id], test);
      qc.invalidateQueries({ queryKey: ["connections", workspace] });
    },
  });
}

/** Queues a test for a connection whose id is only known at call time (e.g. right after create). */
export function useQueueConnectionTest(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`${ws(workspace)}/connections/${id}/test`, ConnectionTest, { method: "POST" }),
    onSuccess: (test, id) => {
      qc.setQueryData(["connection-test", workspace, id], test);
      qc.invalidateQueries({ queryKey: ["connections", workspace] });
    },
  });
}

export function useCreateConnection(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectionInput) =>
      api(`${ws(workspace)}/connections`, ConnectionDetail, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (detail) => {
      qc.setQueryData(["connection", workspace, detail.id], detail);
      qc.invalidateQueries({ queryKey: ["connections", workspace] });
    },
  });
}

export function useConnectionsAction(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectionsAction) =>
      api(`${ws(workspace)}/connections/actions`, okSchema, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connections", workspace] }),
  });
}

/** Header and tab counts. Rows come from the per-tab queries below, on demand. */
export const membersOverviewQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["members-page", workspace],
    queryFn: () => api(`${ws(workspace)}/members-page`, MembersOverview),
  });

export interface PeopleFilters {
  q: string;
  role: string;
  status: string;
  limit: number;
}

export const membersPeopleQuery = (workspace: string, f: PeopleFilters, enabled = true) =>
  queryOptions({
    queryKey: ["members-people", workspace, f],
    queryFn: () => {
      const sp = new URLSearchParams({
        q: f.q,
        role: f.role,
        status: f.status,
        limit: String(f.limit),
      });
      return api(`${ws(workspace)}/members-page/people?${sp}`, MembersPeoplePage);
    },
    enabled,
    placeholderData: (prev) => prev,
  });

export const membersGroupsQuery = (workspace: string, enabled = true) =>
  queryOptions({
    queryKey: ["members-groups", workspace],
    queryFn: () => api(`${ws(workspace)}/members-page/groups`, z.array(WorkspaceGroup)),
    enabled,
  });

export const membersServiceAccountsQuery = (workspace: string, enabled = true) =>
  queryOptions({
    queryKey: ["members-sas", workspace],
    queryFn: () => api(`${ws(workspace)}/members-page/service-accounts`, z.array(ServiceAccount)),
    enabled,
  });

export const membersRequestsQuery = (workspace: string, enabled = true) =>
  queryOptions({
    queryKey: ["members-requests", workspace],
    queryFn: () => api(`${ws(workspace)}/members-page/requests`, z.array(AccessRequest)),
    enabled,
  });

export const membersRolesQuery = (workspace: string, enabled = true) =>
  queryOptions({
    queryKey: ["members-roles", workspace],
    queryFn: () => api(`${ws(workspace)}/members-page/roles`, z.array(RoleDefinition)),
    enabled,
  });

export function useMembersAction(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MembersAction) =>
      api(`${ws(workspace)}/members/actions`, okSchema, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      for (const key of [
        "members-page",
        "members-people",
        "members-groups",
        "members-sas",
        "members-requests",
        "members-roles",
        "audit",
      ])
        qc.invalidateQueries({ queryKey: [key, workspace] });
    },
  });
}

export const channelsPageQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["channels-page", workspace],
    queryFn: () => api(`${ws(workspace)}/channels-page`, z.array(WorkspaceChannel)),
  });

export function useCreateChannel(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChannelInput) =>
      api(`${ws(workspace)}/channels`, Channel, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels-page", workspace] });
      qc.invalidateQueries({ queryKey: ["channels", workspace] });
    },
  });
}

export function useChannelsAction(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChannelsAction) =>
      api(`${ws(workspace)}/channels/actions`, okSchema, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels-page", workspace] });
      qc.invalidateQueries({ queryKey: ["channels", workspace] });
    },
  });
}

export const billingQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["billing", workspace],
    queryFn: () => api(`${ws(workspace)}/billing`, BillingPage),
  });
export const auditQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["audit", workspace],
    queryFn: () => api(`${ws(workspace)}/audit`, AuditPage),
  });
export const workspaceSettingsQuery = (workspace: string) =>
  queryOptions({
    queryKey: ["ws-settings", workspace],
    queryFn: () => api(`${ws(workspace)}/settings`, WorkspaceSettingsPage),
  });

export function useWorkspaceSettingsAction(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceSettingsAction) =>
      api(`${ws(workspace)}/settings/actions`, okSchema, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ws-settings", workspace] });
      qc.invalidateQueries({ queryKey: ["audit", workspace] });
    },
  });
}
